import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ListIcon, XIcon } from '@phosphor-icons/react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { UserMenuDropdown } from './UserMenuDropdown';
import {
    NAVIGATION_BAR_CONFIG,
    NavItem,
    NavLink,
    NavDropdown,
    isNavDropdown,
} from '@/constants/navigation';
import { AppRoutes } from '@/constants/routes';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { ORG_OWNER_ROLE, usePermissions } from '@/hooks/usePermissions';
import { Permission } from '@/types/permissions';

interface NavLinkItemProps {
    link: NavLink;
    isActive: boolean;
    label: string;
    onClick?: () => void;
    variant?: 'desktop' | 'mobile';
}

function NavLinkItem({
    link,
    isActive,
    label,
    onClick,
    variant = 'desktop',
}: NavLinkItemProps) {
    const IconComponent = link.icon;
    const isMobile = variant === 'mobile';

    return (
        <Link
            to={link.path}
            onClick={onClick}
            className={cn(
                'text-sm font-medium',
                'group flex items-center h-8 rounded-[5px] transition-colors',
                isMobile
                    ? 'py-2 pl-2 pr-3 mx-2 gap-1.5'
                    : 'py-1.5 pr-2.5 pl-1.5 gap-[5px]',
                isActive
                    ? 'bg-grey-600 text-white-100'
                    : cn(
                          'hover:text-white-100 hover:bg-grey-600/50',
                          isMobile ? 'text-grey-400' : 'text-grey-300',
                      ),
            )}
        >
            <IconComponent
                className={cn(
                    'size-4 transition-colors flex-shrink-0',
                    isActive
                        ? 'text-white-100'
                        : cn(
                              'group-hover:text-white-100',
                              isMobile ? 'text-grey-400' : 'text-grey-300',
                          ),
                )}
                weight="fill"
            />
            <span className="text-sm font-medium">{label}</span>
        </Link>
    );
}

interface NavDropdownItemProps {
    dropdown: NavDropdown;
    isActive: boolean;
    label: string;
    isActiveLink: (path: string) => boolean;
    variant?: 'desktop' | 'mobile';
    onItemClick?: () => void;
}

function NavDropdownItem({
    dropdown,
    isActive,
    label,
    isActiveLink,
    variant = 'desktop',
    onItemClick,
}: NavDropdownItemProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const IconComponent = dropdown.icon;
    const isMobile = variant === 'mobile';

    if (isMobile) {
        return (
            <div className="flex flex-col">
                <div
                    className={cn(
                        'text-sm font-medium',
                        'group flex items-center h-8 rounded-[5px]',
                        'py-2 pl-2 pr-3 mx-2 gap-1.5',
                        isActive ? 'text-white-100' : 'text-grey-400',
                    )}
                >
                    <IconComponent
                        className={cn(
                            'size-4 flex-shrink-0',
                            isActive ? 'text-white-100' : 'text-grey-400',
                        )}
                        weight="fill"
                    />
                    <span className="text-sm font-medium">{label}</span>
                </div>
                <div className="pl-6">
                    {dropdown.items.map((item) => (
                        <NavLinkItem
                            key={item.path}
                            link={item}
                            isActive={isActiveLink(item.path)}
                            label={t(item.labelKey)}
                            onClick={onItemClick}
                            variant="mobile"
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <button
                    className={cn(
                        'text-sm font-medium',
                        'group flex items-center h-8 rounded-[5px] transition-colors',
                        'py-1.5 pr-2 pl-1.5 gap-[5px]',
                        isActive
                            ? 'bg-grey-600 text-white-100'
                            : 'hover:text-white-100 hover:bg-grey-600/50 text-grey-300',
                    )}
                >
                    <IconComponent
                        className={cn(
                            'size-4 transition-colors flex-shrink-0',
                            isActive
                                ? 'text-white-100'
                                : 'group-hover:text-white-100 text-grey-300',
                        )}
                        weight="fill"
                    />
                    <span className="text-sm font-medium">{label}</span>
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                side="bottom"
                align="start"
                sideOffset={8}
                className="origin-top-left min-w-[180px] z-50"
            >
                <DropdownMenuGroup>
                    {dropdown.items.map((item) => {
                        const ItemIcon = item.icon;
                        const itemIsActive = isActiveLink(item.path);
                        return (
                            <DropdownMenuPrimitive.Item key={item.path} asChild>
                                <Link
                                    to={item.path}
                                    className={cn(
                                        'flex items-center gap-1.5 px-3 py-2 rounded-md outline-none cursor-pointer',
                                        'text-sm font-medium transition-colors',
                                        itemIsActive
                                            ? 'bg-grey-600 text-white-100'
                                            : 'text-grey-200 hover:bg-grey-600 hover:text-white-100',
                                    )}
                                >
                                    <ItemIcon
                                        className="size-4"
                                        weight="fill"
                                    />
                                    <span>{t(item.labelKey)}</span>
                                </Link>
                            </DropdownMenuPrimitive.Item>
                        );
                    })}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

interface NavigationBarProps {
    logoSrc?: string;
    navLinks?: NavItem[];
}

export function NavigationBar({
    logoSrc = NAVIGATION_BAR_CONFIG.logoSrc,
    navLinks = NAVIGATION_BAR_CONFIG.navLinks,
}: NavigationBarProps = {}) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const { user, logout } = useAuth();
    const { has } = usePermissions();
    const { alertsEnabled } = useFeatureFlags();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const canViewSettings = has(Permission.ORGANIZATION_SETTINGS_VIEW);
    const userAvatar = user ? { name: user.name } : null;

    const visibleNavLinks = navLinks.filter(
        (item) =>
            alertsEnabled ||
            isNavDropdown(item) ||
            item.path !== AppRoutes.ALERT_RULES,
    );

    const isActiveLink = (path: string) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return (
            location.pathname === path ||
            location.pathname.startsWith(path + '/')
        );
    };

    const isDropdownActive = (dropdown: NavDropdown) => {
        return dropdown.items.some((item) => isActiveLink(item.path));
    };

    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    return (
        <>
            <nav className="shrink-0 bg-grey-900">
                <div className="h-full flex items-center justify-between p-4">
                    <div className="flex items-center gap-6">
                        <Link
                            to="/"
                            className="flex-shrink-0 flex items-center"
                        >
                            <img
                                src={logoSrc}
                                alt="Logo"
                                className="h-11 w-auto"
                            />
                        </Link>

                        <div className="hidden md:flex items-center gap-2">
                            {visibleNavLinks.map((item, index) =>
                                isNavDropdown(item) ? (
                                    <NavDropdownItem
                                        key={`dropdown-${index}`}
                                        dropdown={item}
                                        isActive={isDropdownActive(item)}
                                        label={t(item.labelKey)}
                                        isActiveLink={isActiveLink}
                                        variant="desktop"
                                    />
                                ) : (
                                    <NavLinkItem
                                        key={item.path}
                                        link={item}
                                        isActive={isActiveLink(item.path)}
                                        label={t(item.labelKey)}
                                        variant="desktop"
                                    />
                                ),
                            )}
                        </div>
                    </div>

                    <div className="absolute right-4 flex items-center gap-8">
                        {user && (
                            <div className="hidden md:block">
                                <UserMenuDropdown
                                    name={user.name}
                                    email={user.email}
                                    organization={user.organization}
                                    isOrgOwner={user.roles.includes(
                                        ORG_OWNER_ROLE,
                                    )}
                                    canViewSettings={canViewSettings}
                                    onLogout={logout}
                                />
                            </div>
                        )}

                        <button
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="md:hidden p-2 text-grey-400 hover:text-white-100 transition-colors"
                            aria-label={t('ariaLabels.openMenu')}
                        >
                            <ListIcon className="size-6" weight="bold" />
                        </button>
                    </div>
                </div>
            </nav>

            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-[60] md:hidden"
                    onClick={closeMobileMenu}
                />
            )}

            <div
                className={cn(
                    'fixed top-0 right-0 h-full w-72 bg-grey-800 z-[70] transform transition-transform duration-300 ease-in-out md:hidden',
                    isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full',
                )}
            >
                <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-4 border-b border-grey-700">
                        <img src={logoSrc} alt="Logo" className="h-11 w-auto" />
                        <button
                            onClick={closeMobileMenu}
                            className="p-2 text-grey-400 hover:text-white-100 transition-colors"
                            aria-label={t('ariaLabels.closeMenu')}
                        >
                            <XIcon className="size-6" weight="bold" />
                        </button>
                    </div>

                    <div className="flex-1 py-4">
                        {visibleNavLinks.map((item, index) =>
                            isNavDropdown(item) ? (
                                <NavDropdownItem
                                    key={`dropdown-${index}`}
                                    dropdown={item}
                                    isActive={isDropdownActive(item)}
                                    label={t(item.labelKey)}
                                    isActiveLink={isActiveLink}
                                    variant="mobile"
                                    onItemClick={closeMobileMenu}
                                />
                            ) : (
                                <NavLinkItem
                                    key={item.path}
                                    link={item}
                                    isActive={isActiveLink(item.path)}
                                    label={t(item.labelKey)}
                                    onClick={closeMobileMenu}
                                    variant="mobile"
                                />
                            ),
                        )}
                    </div>

                    {userAvatar && (
                        <div className="p-4 border-t border-grey-700">
                            <UserMenuDropdown
                                name={userAvatar.name}
                                email={user?.email}
                                isOrgOwner={user?.roles.includes(
                                    ORG_OWNER_ROLE,
                                )}
                                canViewSettings={canViewSettings}
                                onLogout={logout}
                                side="top"
                                showName
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
